import { useEffect, useMemo, useRef, useState } from "react";
import { Room, RoomEvent, DataPacket_Kind, RemoteParticipant } from "livekit-client";
import {
	Annotation,
	AnnotationMessage,
	PRESENTATION_ANNOTATIONS_TOPIC,
} from "./presentationAnnotationTypes";

const textDecoder = new TextDecoder();

/**
 * Read-only listener for presentation annotation data packets (egress /
 * composite template). Mirrors the receive side of livekit-client's hook.
 */
export function usePresentationAnnotationsReceive(
	room: Room | undefined,
	presentationId: string,
	currentPage: number
) {
	const [annotations, setAnnotations] = useState<Annotation[]>([]);
	const annotationsRef = useRef<Annotation[]>(annotations);
	annotationsRef.current = annotations;
	const presentationIdRef = useRef(presentationId);
	presentationIdRef.current = presentationId;

	useEffect(() => {
		setAnnotations([]);
		annotationsRef.current = [];
	}, [presentationId]);

	useEffect(() => {
		if (!room) return;

		const handleDataReceived = (
			payload: Uint8Array,
			_participant?: RemoteParticipant,
			_kind?: DataPacket_Kind,
			topic?: string
		) => {
			if (topic !== PRESENTATION_ANNOTATIONS_TOPIC) return;
			let msg: AnnotationMessage;
			try {
				msg = JSON.parse(textDecoder.decode(payload));
			} catch {
				return;
			}
			if (!msg || msg.presentationId !== presentationIdRef.current) {
				return;
			}
			switch (msg.type) {
				case "add": {
					const { annotation } = msg;
					setAnnotations((prev) => {
						if (prev.some((a) => a.id === annotation.id)) return prev;
						const next = [...prev, annotation];
						annotationsRef.current = next;
						return next;
					});
					break;
				}
				case "remove": {
					const { id } = msg;
					setAnnotations((prev) => {
						const next = prev.filter((a) => a.id !== id);
						annotationsRef.current = next;
						return next;
					});
					break;
				}
				case "clear": {
					const { page } = msg;
					setAnnotations((prev) => {
						const next =
							page == null ? [] : prev.filter((a) => a.page !== page);
						annotationsRef.current = next;
						return next;
					});
					break;
				}
				case "sync": {
					annotationsRef.current = msg.annotations;
					setAnnotations(msg.annotations);
					break;
				}
				default:
					break;
			}
		};

		room.on(RoomEvent.DataReceived, handleDataReceived);
		return () => {
			room.off(RoomEvent.DataReceived, handleDataReceived);
		};
	}, [room]);

	const currentPageAnnotations = useMemo(
		() => annotations.filter((a) => a.page === currentPage),
		[annotations, currentPage]
	);

	return { currentPageAnnotations };
}
